import React, { useRef, useState, useCallback } from "react";
import { AiOutlineFile, AiOutlineDelete, AiOutlineEdit, AiOutlineDownload } from "react-icons/ai";
import { Checkbox, Spinner, SpinnerSize } from "@fluentui/react";

import { StyledFile } from "./Tree.style";
import { useTreeContext } from "./TreeContext";
import { ActionsWrapper, StyledName } from "./Tree.style";
import { PlaceholderInput } from "./TreePlaceholderInput";
import FILE_ICONS from "./FileIcons";

interface FileNameProps {
  name: string;
  ext: string;
  onClick: () => void;
}

const FileName: React.FC<FileNameProps> = React.memo(({ name, ext, onClick }) => {
  return (
    <StyledName onClick={onClick}>
      {FILE_ICONS[ext as keyof typeof FILE_ICONS] ? (
        <div className="colorSecondary" style={{ fontSize: "1.1rem" }}>
          {FILE_ICONS[ext as keyof typeof FILE_ICONS]}
        </div>
      ) : (
        <div className="colorPrimary" style={{ fontSize: "1.1rem" }}>
          <AiOutlineFile />
        </div>
      )}
      &nbsp;&nbsp;{name}
    </StyledName>
  );
});

interface NonEditingContentProps {
  name: string;
  ext: string;
  isImparative: boolean;
  processing: boolean;
  onClick: () => void;
  onEdit: () => void;
  onDownload: () => void;
  onDelete: () => void;
}

const NonEditingContent: React.FC<NonEditingContentProps> = React.memo(({ 
  name, 
  ext, 
  isImparative, 
  processing, 
  onClick, 
  onEdit, 
  onDownload, 
  onDelete 
}) => {
  return (
    <ActionsWrapper>
      <FileName
        name={name}
        ext={ext}
        onClick={onClick}
      />
      {processing ? (
        <div className="marginLeftM displayFlex alignItemsCenter">
          <Spinner size={SpinnerSize.xSmall} />
          <div className="marginLeftS colorPrimary">Processing</div>
        </div>
      ) : null}
      {isImparative && (
        <div className="actions">
          <AiOutlineEdit onClick={onEdit} />
          <AiOutlineDownload onClick={onDownload} />
          <AiOutlineDelete onClick={onDelete} />
        </div>
      )}
    </ActionsWrapper>
  );
});

interface EditingContentProps {
  name: string;
  onSubmit: (name: string) => Promise<void>;
}

const EditingContent: React.FC<EditingContentProps> = React.memo(({ 
  name, 
  onSubmit 
}) => {
  console.log('EditingContent rendering');
  return (
    <PlaceholderInput
      defaultValue={name}
      type="file"
      style={{ marginLeft: 0 }}
      handleSubmit={onSubmit}
    />
  );
});

interface FileProps {
  name: string;
  id: string;
  parent?: any;
  selected?: boolean;
  processing?: boolean;
  onSelect?: (checked: boolean) => void;
  onEdit?: () => void;
  onDelete?: (deleteCallback: () => void) => void;
  onRename?: (name: string) => Promise<void>;
  onDownload?: () => void;
  onClick?: () => void;
}

const File: React.FC<FileProps> = React.memo(({ 
  name, 
  id, 
  parent = null,
  selected = false,
  processing = false,
  onSelect = () => {}, 
  onEdit = () => {}, 
  onDelete = () => {},
  onRename = async () => {}, 
  onDownload = () => {}, 
  onClick = () => {} 
}) => {
  const [isEditing, setEditing] = useState(false);
  const { state, dispatch, isImparative, onNodeClick } = useTreeContext();
  const ext = useRef("");

  let splitted = name && name.split(".");
  ext.current = splitted && splitted[splitted.length - 1];

  const handleRename = useCallback(async (newName: string) => {
    await onRename(newName);
    dispatch({ type: "EDIT_FILE", payload: { id, name: newName } });
    setEditing(false);
  }, [onRename, dispatch, id]);

  const handleDelete = useCallback(() => {
    onDelete(() => {
      dispatch({ type: "DELETE_FILE", payload: { id } });
    });
  }, [onDelete, dispatch, id]);

  const handleClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    onClick();
    onNodeClick({
      state,
      name,
      parent,
      type: "file"
    });
  }, [onClick, onNodeClick, state, name, parent]);

  return (
    <div className="displayFlex">
      <Checkbox 
        className="marginRightS" 
        checked={selected} 
        styles={{ root: { marginTop: '3px' } }} 
        onChange={(_, checked) => onSelect(checked || false)} 
      />
      <StyledFile
        onClick={handleClick}
        className="tree__file"
      >
        {isEditing ? (
          <EditingContent
            name={name}
            onSubmit={handleRename}
          />
        ) : (
          <NonEditingContent
            name={name}
            ext={ext.current}
            isImparative={isImparative}
            processing={processing}
            onClick={onClick}
            onEdit={() => setEditing(true)}
            onDownload={onDownload}
            onDelete={handleDelete}
          />
        )}
      </StyledFile>
    </div>
  );
});

export { File };
